<script setup lang="ts">
import { usePlayerFlagsStore } from '@/stores/playerFlagsStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import Alert from '@/components/Elements/Alerts/Alert.vue'
import TutorialFrame from '@/components/Interface/Modal/Modals/Tutorial/TutorialFrame.vue'
import PlanetSizeAnimation from '@/components/Elements/Animations/Planet/PlanetSizeAnimation.vue'
import { computed } from 'vue'
import { handleNext } from '../tutorialSteps'

const playerFlagsStore = usePlayerFlagsStore()
const gameStore = useGameStore()
const assetStore = useAssetStore()

const handleNextStep = async () => {
  await handleNext('tutorial_planet_types')
}

// Get the homeworld planet type name
const homeWorldPlanetType = computed(() => {
  if (!gameStore.player) return 'Unknown'
  const planetType = assetStore.findPlanetTypeById(gameStore.player.homeWorldPlanetType)
  return planetType?.name || 'Unknown'
})

// Get the opposite (bad) planet type name
const oppositePlanetType = computed(() => {
  if (!gameStore.player) return 'Unknown'
  const planetType = assetStore.findPlanetTypeById(gameStore.player.homeWorldOppositePlanetType)
  return planetType?.name || 'Unknown'
})

// Get the homeworld gravity type based on homeWorldPlanetSize
const homeWorldCompatibility = computed(() => {
  if (!gameStore.player) return 'standard gravity'
  
  // Map homeWorldPlanetSize to gravity type
  // Size 1 = low gravity, Size 3 = high gravity
  if (gameStore.player.homeWorldPlanetSize === 1) return 'low gravity'
  if (gameStore.player.homeWorldPlanetSize === 3) return 'high gravity'
  return 'standard gravity'
})

// Get opposite gravity type based on homeWorldOppositePlanetSize
const oppositeGravity = computed(() => {
  if (!gameStore.player) return 'extreme gravity'
  
  // Map homeWorldOppositePlanetSize to gravity type
  // Size 1 = low gravity, Size 3 = high gravity
  if (gameStore.player.homeWorldOppositePlanetSize === 1) return 'low gravity'
  if (gameStore.player.homeWorldOppositePlanetSize === 3) return 'high gravity'
  return 'standard gravity'
})

</script>

<template>
  <TutorialFrame
    video-url="https://cdn.ferion.com/videos/advisors/temperate/colonizing.webm"
  >
    <template #text>
      <p class="pb-2">
        It's time that we settle more planets, so we can grow faster!
      </p>
      <div class="flex justify-center items-center pb-2">
        <div class="h-24 w-24 flex justify-center items-center">
          <PlanetSizeAnimation 
            v-if="gameStore.player"
            :size="gameStore.player.homeWorldPlanetSize" 
            :type="gameStore.player.homeWorldPlanetType"
          />
        </div>
      </div>
      <p class="pb-2">
        Our beautiful homeworld is a <span class="text-green-400">{{ homeWorldPlanetType }}</span> world with {{ homeWorldCompatibility }} and our people thrive in this environment.
      </p>
      <p class="pb-2">
        However we absolutely cannot live on <span class="text-red-400">{{ oppositePlanetType }}</span> worlds with {{ oppositeGravity }}. Keep this in mind when picking planets to settle.
      </p>
    </template>

    <template #alerts>
      <Alert type="info">
        Choose your colonies wisely! Planet types and sizes affect production, population growth, and strategic value.
      </Alert>
    </template>

    <template #buttons>
      <ActionButton @click="handleNextStep">Got it!</ActionButton>
    </template>
  </TutorialFrame>
</template>

<style scoped lang="scss"></style>